<script lang="ts" context="module">
	import NewsletterSmoreBranding from "$lib/components/show/NewsletterSmoreBranding.svelte";
	import { createShowContext } from "$lib/components/show/showContext";
	import ShowPageToolbar from "$lib/components/show/ShowPageToolbar.svelte";
	import { createSocialButtonContext } from "$lib/components/show/socialContext";
	import { onMount, tick } from "svelte";
	import ContactAuthorModal from "$lib/components/show/ContactAuthorModal.svelte";
	import SubscribeToListModal from "$lib/components/show/SubscribeToListModal.svelte";
	import TableOfContent from "$lib/components/show/TableOfContent.svelte";
	import { handleVideoClick } from "$lib/components/show/videoUtils";
	import { ServerError, postJson } from "$lib/components/http";
	import PollBlockDynamicReplacer from "$lib/components/show/PollBlockDynamicReplacer.svelte";
	import ShowPageBanner from "$lib/components/show/ShowPageBanner.svelte";
	import { page } from "$app/stores";
	import { getClassicWebUrl } from "$lib/components/currentUser";
	import FancyBox from "$lib/components/FancyBox.svelte";

	const codeToMessage: Record<string, string> = {
		event_not_found: "Couldn't find this event. Try refreshing the page.",
		attendee_not_found: "Couldn't find this attendee.",
		invalid_email: "Your email doesn't appear to be valid.",
		empty_first_name: "First name can't be empty.",
		empty_last_name: "Last name can't be empty.",
		double_rsvp: "This email has already RSVPed. To change status, see your confirmation email."
	};

	const ANIMATION_DURATION = 1000;
	const MESSAGE_DISPLAY_DURATION = 2500;
</script>

<script lang="ts">
	const CLASSIC_WEB_URL = getClassicWebUrl();

	const {
		mode,
		html,
		css,
		feel,
		author,
		newsletter,
		doNotCountView,
		brandingMode,
		showSharingOptions,
		showTranslationOptions,
		showAccessibilityOptions,
		screenshotUrl,
		facebookThumbnailUrl,
		showViewCount,
		viewCount,
		showTOC,
		isTranslateView,
		translateTarget,
		hideShowPageTopBar,
		hasPolls,
		canonicalUrl,
		shortUrl,
		metaTitle,
		useCustomFavicon,
		pageTitle,
		pageDescription,
		noIndexing,
		publishedAt,
		viewTrackingUrl
	} = $page.data;

	let hasContactAuthorButton: boolean = false;
	let hasSubscribeToListButton: boolean = false;

	let isContactAuthorOpen: boolean = false;
	let isSubscribeToListOpen: boolean = false;
	let listId: string = "";

	createShowContext({
		brandingMode,
		showSharingOptions,
		showTranslationOptions,
		showAccessibilityOptions,
		isTranslateView,
		showTOC,
		showViewCount,
		viewCount,
		hideShowPageTopBar,
		hasPolls,
		newsletterId: newsletter.id
	});

	createSocialButtonContext({
		newsletterId: newsletter.id,
		shortUrl,
		metaTitle,
		canonicalUrl,
		screenshotUrl
	});

	onMount(() => {
		// Inject the CSS into the head element
		const headElement = document.getElementsByTagName("head")[0];
		const styleElements = headElement.getElementsByTagName("style");
		let styleElement;

		if (styleElements.length === 0) {
			styleElement = document.createElement("style");
			headElement.appendChild(styleElement);
		} else {
			styleElement = styleElements[0];
		}

		styleElement.innerHTML += css;

		// TableOfContents component use the body's "show-page" class to distinguish between "show" and "show-author" pages  -->
		document.body.classList.add("show-page");

		// Extract anchor from URL
		const urlParts = document.URL.split("#");
		let urlAnchor;
		if (urlParts.length > 1) {
			urlAnchor = urlParts[1];
		}

		// Handle "Contact Author" click in the email
		const contactUserButton = document.querySelector("#contact_user");
		if (contactUserButton) {
			hasContactAuthorButton = true;
			contactUserButton.addEventListener("click", showContactAuthorModal);
			if (urlAnchor === "contact_user") {
				window.requestAnimationFrame(async () => {
					await tick();
					(contactUserButton as HTMLButtonElement).click();
				});
			}
		}

		// Handle "Subscribe to Author" click in the email
		const subscribeUserButton = document.querySelector("#subscribe_user");
		if (subscribeUserButton) {
			hasSubscribeToListButton = true;
			listId = (subscribeUserButton as HTMLElement).dataset.listId || "";
			subscribeUserButton.addEventListener("click", showSubscribeToListModal);
			if (urlAnchor === "subscribe_user") {
				window.requestAnimationFrame(async () => {
					await tick();
					(subscribeUserButton as HTMLButtonElement).click();
				});
			}
		}

		// Handle Video click in the email
		const videoBlockAnchor = document.querySelector(`#${urlAnchor}`);
		if (videoBlockAnchor) {
			const videoButton = document.querySelector(`[data-block-id=${urlAnchor}] button`);
			if (videoButton) {
				handleVideoClick(videoButton);
			}
		}

		registerRSVPEvents();

		return () => {
			// @Eli, I don't think we need this as the main component never dismounts
			unregisterRSVPEvents();
		};
	});

	// Handle tracked url fixes
	async function handleClicks(e: MouseEvent | TouchEvent) {
		const targetElement = e.target;
		if (!targetElement) {
			return;
		}

		// Handle links
		const linkWrapper = (targetElement as HTMLElement).closest("a.skip-tracking[track-href]");
		if (linkWrapper) {
			// @ts-ignore
			if (linkWrapper.classList.contains("fancy-pic") && e.buttons === 1) {
				return;
			}
			(linkWrapper as HTMLAnchorElement).href = linkWrapper.getAttribute("track-href") || linkWrapper.href;
		}

		handleVideoClick(targetElement);
	}

	function showContactAuthorModal() {
		isContactAuthorOpen = true;
	}

	function showSubscribeToListModal(e: Event) {
		const evt = e as MouseEvent;
		if (listId.trim().length === 0) {
			listId = (e.target as HTMLButtonElement).dataset.listId || "";
		}
		isSubscribeToListOpen = true;
	}

	async function submitRSVPForm(e: Event) {
		e.stopImmediatePropagation();
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const button = (e as SubmitEvent).submitter as HTMLButtonElement;
		const firstNameInput = form.querySelector(".first-name") as HTMLInputElement;
		const lastNameInput = form.querySelector(".last-name") as HTMLInputElement;
		const emailInput = form.querySelector(".email-address") as HTMLInputElement;
		const rsvpFormContainer = form.closest(".event-rsvp-container") as HTMLDivElement;
		if (!rsvpFormContainer) {
			console.error("RSVP container not found");
			return;
		}

		const pageId = form.dataset.pageId;
		const eventId = form.dataset.eventId;

		const firstName = firstNameInput.value;
		const lastName = lastNameInput.value;
		const email = emailInput.value;

		const rsvpMessage = form.querySelector(".rsvp-message");
		if (rsvpMessage) {
			form.removeChild(rsvpMessage);
		}

		const buttonText = button.value;
		try {
			button.classList.remove("bg-green-600");
			button.classList.add("bg-nxgray-300", "pointer-events-none");
			button.value = "Please wait...";
			await postJson(`${CLASSIC_WEB_URL}/app/flyer_actions/rsvp_attend_json`, {
				page: pageId,
				event: eventId,
				firstName,
				lastName,
				email
			});
			const div = showRSVPMessage(rsvpFormContainer, `${email} is now attending this event.`, false);
			handleMessageAnimation(div, rsvpFormContainer, { firstNameInput, lastNameInput, emailInput });
		} catch (e) {
			let errorMsg = codeToMessage[e.code] || ServerError.GENERIC_ERROR;
			const div = showRSVPMessage(rsvpFormContainer, errorMsg, true);
			handleMessageAnimation(div, rsvpFormContainer, { firstNameInput, lastNameInput, emailInput });
		} finally {
			button.value = buttonText;
			button.classList.add("bg-green-600");
			button.classList.remove("bg-nxgray-300", "pointer-events-none");
		}
	}

	function showRSVPMessage(container: HTMLDivElement, message: string, isError: boolean = false) {
		// We inject new div element to the DOM since there are published newsletters with the FORM element already out there
		// and we can't use native svelte class condition
		const div = document.createElement("div");
		div.classList.add(
			isError ? "bg-red-500" : "bg-green-600",
			"text-white",
			"w-full",
			"py-3",
			"text-sm",
			"text-center",
			"rsvp-message",
			"top-0",
			"left-0",
			"right-0",
			"duration-1000",
			"transition-all",
			"opacity-0",
			"absolute"
		);
		div.innerText = message;
		container.appendChild(div);
		return div;
	}

	function clearFormInputs(inputs: { firstNameInput: HTMLInputElement; lastNameInput: HTMLInputElement; emailInput: HTMLInputElement }) {
		inputs.firstNameInput.value = "";
		inputs.firstNameInput.focus();
		inputs.lastNameInput.value = "";
		inputs.emailInput.value = "";
	}

	function handleMessageAnimation(
		div: HTMLDivElement,
		container: HTMLDivElement,
		inputs: { firstNameInput: HTMLInputElement; lastNameInput: HTMLInputElement; emailInput: HTMLInputElement }
	) {
		window.requestAnimationFrame(() => {
			div.classList.remove("opacity-0");
			setTimeout(() => {
				clearFormInputs(inputs);
				div.classList.add("opacity-0");
				setTimeout(() => {
					container.removeChild(div);
				}, ANIMATION_DURATION);
			}, MESSAGE_DISPLAY_DURATION);
		});
	}

	function registerRSVPEvents() {
		const rsvpForms = document.querySelectorAll(".new-event-rsvp-form");
		rsvpForms.forEach((form) => {
			const rsvpFormContainer = form.closest(".event-rsvp-container") as HTMLDivElement;
			rsvpFormContainer.classList.add("relative");
			form.addEventListener("submit", submitRSVPForm);
		});
	}

	function unregisterRSVPEvents() {
		const rsvpForms = document.querySelectorAll(".new-event-rsvp-form");
		rsvpForms.forEach((form) => {
			form.removeEventListener("submit", submitRSVPForm);
		});
	}
</script>

{#if mode === "screenshot"}
	<!--TODO: Implement screenshot mode  -->
	<!-- 
	${screenshot_show_utils.remove_non_required_elements()}
	${screenshot_show_utils.screenshot_wait_util()}
	${screenshot_show_utils.screenshot_stylesheets()} 
	-->
{/if}

<svelte:head>
	{#if useCustomFavicon}
		<link rel="shortcut icon" type="image/x-icon" href="https://cdn.smore.com/show-favicon.ico" />
	{/if}

	<title>{pageTitle}</title>

	<!-- This one is used for the Subject of the newsletter we send -->
	<meta content={metaTitle} id="meta_title" />

	<!-- Facebook OpenGraph meta-data. See: https://developers.facebook.com/docs/sharing/webmasters/ -->
	<meta property="og:title" content={metaTitle} />
	<meta property="og:type" content="article" />
	<meta property="og:url" content={canonicalUrl} />
	<meta property="og:image" content={facebookThumbnailUrl} />
	<meta property="og:image:width" content="500" />
	<meta property="og:image:height" content="262" />
	<meta property="og:site_name" content="Smore" />
	<meta property="og:description" content={metaTitle} />
	<meta property="og:updated_time" content={publishedAt} />
	<meta property="article:published_time" content={publishedAt} />

	{#if brandingMode === "full"}
		<meta property="article:publisher" content="https://www.facebook.com/smorepages" />
	{/if}

	<meta name="keywords" content="" />
	<meta name="description" content={pageDescription} />

	<link rel="canonical" href={canonicalUrl} />
	<meta name="shorturl" content={shortUrl} />

	<meta name="twitter:card" content="photo" />
	<!-- smore's user-id -->
	<meta name="twitter:site:id" content="362324841" />
	<meta name="twitter:url" content={canonicalUrl} />
	<meta name="twitter:title" content={metaTitle} />
	<meta name="twitter:description" content={metaTitle} />
	<meta name="twitter:image" content={facebookThumbnailUrl} />
	<meta name="twitter:image:width" content="500" />
	<meta name="twitter:image:height" content="262" />

	<!-- prefetch dns -->
	<link rel="dns-prefetch" href="//s3.amazonaws.com" />
	<link rel="dns-prefetch" href="//cdn.api.twitter.com" />
	<link rel="dns-prefetch" href="//graph.facebook.com" />
	<link rel="dns-prefetch" href="//connect.facebook.net" />
	<link rel="dns-prefetch" href="//partners-api.pinterest.com" />
	<link rel="dns-prefetch" href="//google-analytics.com" />

	{#if !noIndexing}
		<!-- Blocking indexing is done in the parent layout. However, if indexing is allowed, we still don't want to index the images. -->
		<meta name="robots" content="noimageindex" />
	{/if}

	{#if !doNotCountView}
		<!-- 
		View tracking code
    TODO: Change script to "defer" instead of "async" so it's loaded last?
    See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/script#async_and_defer
  	-->
		<script type="text/javascript" src={viewTrackingUrl} async></script>
	{/if}
</svelte:head>

<svelte:body on:mousedown={handleClicks} on:touchstart={handleClicks} />

<ShowPageToolbar />

<ShowPageBanner isAuthorGhosted={author.isGhosted} isDeleted={newsletter.isDeleted} />

<!-- The actual newsletter HTML -->
<div class="container-show main-show md:p-4">
	<div class="post-container" style="max-width:100%;">
		<div class="display-page display-page-v1" style="max-width:100%;" role="main">
			<div class="page-content-wrap" style="max-width:100%;" id="page-content-wrap">
				<!-- Injection of the actual Newsletter HTML -->
				{@html html}
			</div>
			<div class="clear"></div>
		</div>
	</div>
</div>

<FancyBox />
<PollBlockDynamicReplacer {newsletter} {hasPolls} allowTableOfContent={showTOC} />

{#if showTOC}
	<TableOfContent />
{/if}

{#if hasContactAuthorButton}
	<ContactAuthorModal bind:isOpen={isContactAuthorOpen} />
{/if}

{#if hasSubscribeToListButton}
	<SubscribeToListModal bind:isOpen={isSubscribeToListOpen} bind:listId />
{/if}

<!-- Svelte has started to require a slot, even though we don't need it -->
<slot />

<NewsletterSmoreBranding />

<!-- <style src="./show.css"> -->
<style>
	:global(a:focus, button:focus) {
		outline: auto !important;
		outline-color: #505060 !important;
	}
</style>
